import type { DataProvider, Identifier } from 'ra-core'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { useNotify } from 'react-admin'

import { BULK_EDIT_AUTO_ACCEPT_MIN_VISIBLE_MS } from '../constants'
import { buildBulkEditVisiblePatch, mergeBulkEditInitialValues } from '../helpers'
import {
  listBulkEditEmitSaveFailed,
  listBulkEditEmitSaveSuccess,
  listBulkEditPerformUpdate,
} from '../listBulkEditApply'
import type { BulkEditAutoAcceptPolicy, BulkEditResourceDefinition } from '../types'

type NotifyHandler = ReturnType<typeof useNotify>

type Options = {
  autoAcceptPolicy: BulkEditAutoAcceptPolicy
  resource: string
  initialValues: Record<string, unknown> | undefined
  definition: BulkEditResourceDefinition
  effectiveIds: Identifier[]
  resolving: boolean
  patchVisibilityKeys: string[] | undefined
  dataProvider: DataProvider
  notify: NotifyHandler
  refresh: () => void
  unselectAll: (resource: string) => void
  translate: (key: string, options?: Record<string, unknown>) => string
  onClose: () => void
}

export function useBulkEditAutoAccept(options: Options) {
  const {
    autoAcceptPolicy,
    resource,
    initialValues,
    definition,
    effectiveIds,
    resolving,
    patchVisibilityKeys,
    dataProvider,
    notify,
    refresh,
    unselectAll,
    translate,
    onClose,
  } = options

  const autoAcceptActive = autoAcceptPolicy !== false

  const patch = useMemo(() => {
    if (!autoAcceptActive) return null
    return buildBulkEditVisiblePatch(
      definition,
      mergeBulkEditInitialValues(definition, initialValues),
      patchVisibilityKeys
    )
  }, [autoAcceptActive, definition, initialValues, patchVisibilityKeys])

  const patchIsEmpty = Boolean(autoAcceptActive && patch != null && Object.keys(patch).length === 0)

  const needsUserConfirm = Boolean(
    autoAcceptActive &&
    (autoAcceptPolicy === 'confirm-always' || (autoAcceptPolicy === 'confirm-if-multiple' && effectiveIds.length > 1))
  )

  const [userConfirmedBulk, setUserConfirmedBulk] = useState(!needsUserConfirm)
  const [saveFailed, setSaveFailed] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const generationRef = useRef(0)

  useEffect(() => {
    setUserConfirmedBulk(!needsUserConfirm)
  }, [needsUserConfirm])

  useLayoutEffect(() => {
    if (!autoAcceptActive || effectiveIds.length === 0 || resolving || patchIsEmpty || !userConfirmedBulk) {
      return
    }
    if (!patch) {
      return
    }

    const generation = ++generationRef.current
    let cancelled = false

    setIsApplying(true)
    setSaveFailed(false)

    const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

    const runAutoAccept = async () => {
      let closingAfterSuccess = false
      try {
        const started = Date.now()
        await listBulkEditPerformUpdate(dataProvider, resource, effectiveIds, patch)
        if (cancelled || generation !== generationRef.current) {
          return
        }
        const remaining = Math.max(0, BULK_EDIT_AUTO_ACCEPT_MIN_VISIBLE_MS - (Date.now() - started))
        if (remaining > 0) {
          await delay(remaining)
        }
        if (cancelled || generation !== generationRef.current) {
          return
        }
        listBulkEditEmitSaveSuccess({
          resource,
          recordCount: effectiveIds.length,
          autoAccept: true,
          notify,
          translate,
          refresh,
          unselectAll,
        })
        closingAfterSuccess = true
        onClose()
      } catch (e: unknown) {
        if (!cancelled && generation === generationRef.current) {
          listBulkEditEmitSaveFailed({ resource, recordCount: effectiveIds.length, autoAccept: true })
          setSaveFailed(true)
        }
      } finally {
        if (!cancelled && generation === generationRef.current && !closingAfterSuccess) {
          setIsApplying(false)
        }
      }
    }

    void runAutoAccept()

    return () => {
      cancelled = true
      setIsApplying(false)
    }
  }, [
    autoAcceptActive,
    dataProvider,
    effectiveIds,
    notify,
    onClose,
    patch,
    patchIsEmpty,
    refresh,
    resolving,
    resource,
    translate,
    unselectAll,
    userConfirmedBulk,
  ])

  const awaitingModalConfirm = Boolean(autoAcceptActive && !patchIsEmpty && needsUserConfirm && !userConfirmedBulk)

  return {
    patchIsEmpty,
    saveFailed,
    awaitingModalConfirm,
    acknowledgeBulkConfirm: () => setUserConfirmedBulk(true),
    isApplying,
  }
}
